import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { INIT } from "../data/initialData";

const STORAGE_KEY = "cyclojet_db_v1";

function loadDB() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return INIT;
    const parsed = JSON.parse(raw);
    // merge with INIT so newly added default fields are never missing for older saved data
    return { ...INIT, ...parsed, settings: { ...INIT.settings, ...(parsed.settings || {}) } };
  } catch {
    return INIT;
  }
}

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [db, setDBState] = useState(loadDB);

  // Every db change is written straight to localStorage so admin edits
  // survive reloads and immediately show up on the public site.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
  }, [db]);

  // Keep multiple open tabs/windows (e.g. admin panel + storefront) in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setDBState(JSON.parse(e.newValue)); } catch { /* ignore malformed payload */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setDB = useCallback((updater) => {
    setDBState((d) => (typeof updater === "function" ? updater(d) : updater));
  }, []);

  const saveDB = useCallback((table, row) => {
    setDBState((d) => {
      const rows = d[table] || [];
      const i = rows.findIndex((x) => x.id === row.id);
      const next = i >= 0
        ? rows.map((x, j) => (j === i ? row : x))
        : [...rows, { ...row, id: Math.max(0, ...rows.map((x) => x.id || 0)) + 1 }];
      return { ...d, [table]: next };
    });
  }, []);

  const delDB = useCallback((table, id) => {
    setDBState((d) => ({ ...d, [table]: (d[table] || []).filter((r) => r.id !== id) }));
  }, []);

  const resetDB = useCallback(() => setDBState(INIT), []);

  return (
    <DataContext.Provider value={{ db, setDB, saveDB, delDB, resetDB }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within a DataProvider");
  return ctx;
}
